import {
  ResolvedFormConfig,
  ResolvedFieldConfig,
  PresentationFormConfigSchema,
  ConfigurableFormField,
  AllFormField,
  CONFIGURABLE_FORM_FIELDS
} from '../types';

/**
 * Default field configuration - matches current behavior exactly.
 * Used as a base that API configs are merged into.
 */
const DEFAULT_FIELD_CONFIG: ResolvedFieldConfig = {
  visibility: 'visible',
  minCount: undefined,
  maxCount: undefined,
};

/**
 * Default form configuration that preserves existing behavior.
 * This is used when:
 * - A presentation doesn't have a formConfig
 * - A presentation's formConfig is invalid
 * - A field is missing from the API's formConfig
 * 
 * Note: minCount >= 1 means field is required
 */
export const DEFAULT_FORM_CONFIG: ResolvedFormConfig = {
  retailerIds: {
    visibility: 'visible',
    minCount: 1, // Required - at least 1
    maxCount: undefined, // No limit = multi-select
  },
  entityType: {
    visibility: 'visible',
    minCount: 1, // Required - exactly 1
    maxCount: 1, // Single select
  },
  entityId: {
    visibility: 'visible',
    minCount: undefined, // Conditionally required based on entityType - handled in schema
    maxCount: 1, // Single select
  },
  categoryIds: {
    visibility: 'visible',
    minCount: undefined, // Conditionally required - either categories or subcategories
    maxCount: undefined, // No limit = multi-select
  },
  subcategoryIds: {
    visibility: 'visible',
    minCount: undefined, // Conditionally required - either categories or subcategories
    maxCount: undefined, // No limit = multi-select
  },
  brandIds: {
    visibility: 'hidden', // Brand selection is on Brands step
    minCount: undefined, // Not required by default
    maxCount: undefined, // No limit = multi-select
  },
  brandGroupings: {
    visibility: 'visible',
    minCount: 1, // Required - exactly 1
    maxCount: 1, // Single select
  },
};

/**
 * Merges a single field config from API with the default field config.
 */
const mergeFieldConfig = (
  defaultConfig: ResolvedFieldConfig,
  apiConfig?: Partial<ResolvedFieldConfig>
): ResolvedFieldConfig => {
  if (!apiConfig) return defaultConfig;

  return {
    visibility: apiConfig.visibility ?? defaultConfig.visibility,
    minCount: apiConfig.minCount ?? defaultConfig.minCount,
    maxCount: apiConfig.maxCount ?? defaultConfig.maxCount,
  };
};

/**
 * Validates API config using Zod and merges with defaults.
 * Returns DEFAULT_FORM_CONFIG if API config is invalid or missing.
 *
 * @param apiConfig - The formConfig object from the API response (or undefined)
 * @returns A complete ResolvedFormConfig with all fields guaranteed
 */
export const resolveFormConfig = (apiConfig?: unknown): ResolvedFormConfig => {
  // No config provided - use defaults
  if (!apiConfig) {
    return DEFAULT_FORM_CONFIG;
  }

  // Validate the API config structure
  const parsed = PresentationFormConfigSchema.safeParse(apiConfig);

  if (!parsed.success) {
    console.warn(
      '[Presentations] Invalid formConfig from API, using defaults:',
      parsed.error.issues
    );
    return DEFAULT_FORM_CONFIG;
  }

  const validatedConfig = parsed.data;

  // Deep merge: API values override defaults for each field (configurable fields only)
  const resolvedConfig = CONFIGURABLE_FORM_FIELDS.reduce((acc, fieldName) => {
    acc[fieldName] = mergeFieldConfig(
      DEFAULT_FORM_CONFIG[fieldName],
      validatedConfig[fieldName] as Partial<ResolvedFieldConfig> | undefined
    );
    return acc;
  }, {} as ResolvedFormConfig);

  // entityType is auto-derived, not configurable - start with default
  resolvedConfig.entityType = { ...DEFAULT_FORM_CONFIG.entityType };

  // Auto-hide brandGroupings if brandIds has minCount >= 1 (unless explicitly set)
  const brandIdsRequired = (resolvedConfig.brandIds.minCount ?? 0) >= 1;
  if (brandIdsRequired && !validatedConfig.brandGroupings?.visibility) {
    resolvedConfig.brandGroupings = {
      ...resolvedConfig.brandGroupings,
      visibility: 'hidden',
      minCount: undefined,
    };
  }

  // Auto-hide brandIds if brandGroupings is visible and brandIds wasn't explicitly configured
  if (resolvedConfig.brandGroupings.visibility === 'visible' && !validatedConfig.brandIds?.visibility) {
    resolvedConfig.brandIds = {
      ...resolvedConfig.brandIds,
      visibility: 'hidden',
    };
  }

  // Auto-derive all entity-related fields from entityId visibility
  // If entityId is hidden, hide ALL entity fields (entityType, categoryIds, subcategoryIds)
  // Note: categoryIds/subcategoryIds keep their minCount/maxCount for when entityId is visible
  if (resolvedConfig.entityId.visibility === 'hidden') {
    resolvedConfig.entityType = {
      ...resolvedConfig.entityType,
      visibility: 'hidden',
      minCount: undefined,
    };
    resolvedConfig.categoryIds = {
      ...resolvedConfig.categoryIds,
      visibility: 'hidden',
      // Keep minCount/maxCount from API config for validation when visible
    };
    resolvedConfig.subcategoryIds = {
      ...resolvedConfig.subcategoryIds,
      visibility: 'hidden',
      // Keep minCount/maxCount from API config for validation when visible
    };
  }

  return resolvedConfig;
};

/**
 * Helper to check if a field should be visible based on config.
 */
export const isFieldVisible = (
  config: ResolvedFormConfig,
  fieldName: AllFormField
): boolean => {
  return config[fieldName].visibility === 'visible';
};

/**
 * Helper to get field config for a specific field.
 */
export const getFieldConfig = (
  config: ResolvedFormConfig,
  fieldName: AllFormField
): ResolvedFieldConfig => {
  return config[fieldName];
};
